// Reconnect orchestrator for an SSE stream.
//
// The platform's run events are streamed over SSE from the server. A
// stream can close for two reasons:
//   1. The run finished — server emitted a `run-end` event and closed
//      the connection. This is a CLEAN end; nothing to reconnect.
//   2. Network hiccup, browser tab idle eviction, proxy timeout, etc.
//      The server still has the run running; the client just lost the
//      pipe. Without reconnect, the UI goes dark while the server
//      keeps generating events the operator never sees.
//
// This module wraps a per-pass `streamOnce` function with a reconnect
// loop that distinguishes (1) from (2) and re-attaches the dropped
// stream from the next unseen event. The server's `?since=N` query
// param replays events with `seq >= N`, so a reconnect with
// `since=cursor` resumes exactly where we left off — no duplicates,
// no gaps.
//
// Design rules:
//   - PURE. No fetch, no globals, no timers we can't replace. Tests
//     inject a fake streamOnce + sleep so reconnect behaviour is
//     verified without touching the network.
//   - The `cursor` advances as events arrive. It's the SINGLE source
//     of truth for what to request on reconnect. If the server sends
//     out-of-order events (it doesn't, by contract), the max-seq
//     accumulator still gives us a correct lower bound for replay.
//   - Reconnect cap exists to prevent an INFINITE network loop when
//     the server is genuinely down. This is NOT a cap on the build —
//     the server-side run keeps running; the operator can reload the
//     page to get a fresh stream attached to the same run id. The cap
//     bounds wasted client retries, not the build itself.

export type SseEvent = {
  seq?:  number;
  kind?: string;
  [k: string]: any;
};

// One stream pass: opens the connection, yields events via onEvent,
// resolves when the server closes the stream (clean OR mid-flight),
// rejects on network/HTTP errors. Receives the `since` cursor so the
// server can replay from there.
export type StreamOnce = (
  since:   number,
  onEvent: (e: SseEvent) => void,
  signal:  AbortSignal,
) => Promise<void>;

export type ReconnectOptions = {
  initialSince?: number;
  // Max reconnects after the FIRST pass. Default 5 means: 1 fresh
  // pass + up to 5 reconnects = 6 stream attempts total before giving
  // up. Tune via tests; production picks the default.
  maxRetries?:   number;
  // Backoff between reconnects, ms. Index N applies to the (N+1)th
  // retry. The last entry repeats for any further retries (currently
  // there are none beyond maxRetries, but the array could be shorter
  // than maxRetries safely).
  backoffMs?:    number[];
  // Injectable sleep so tests can fast-forward without real wall time.
  sleep?:        (ms: number) => Promise<void>;
};

// Streams events through onEvent until either:
//   - the server emits a `run-end` event (clean end, returns)
//   - the abort signal fires (returns)
//   - the reconnect budget is exhausted (throws)
//
// The cursor (max seq + 1) is preserved across reconnects so the
// server's `?since=N` replay never duplicates or skips an event.
export async function streamWithReconnect(
  streamOnce: StreamOnce,
  onEvent:    (e: SseEvent) => void,
  signal:     AbortSignal,
  opts:       ReconnectOptions = {},
): Promise<void> {
  let cursor = opts.initialSince ?? 0;
  const maxRetries = opts.maxRetries ?? 5;
  const backoff    = opts.backoffMs  ?? [1000, 2000, 4000, 8000, 16000];
  const sleep      = opts.sleep      ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  let attempts = 0;            // number of RECONNECTS (first pass is not counted)
  let runEnded = false;
  let lastError: unknown;

  // We wrap the caller's onEvent so the cursor advances + run-end is
  // detected without the caller needing to do bookkeeping.
  const wrappedOnEvent = (e: SseEvent) => {
    if (typeof e?.seq === "number" && e.seq + 1 > cursor) cursor = e.seq + 1;
    if (e?.kind === "run-end") runEnded = true;
    onEvent(e);
  };

  // First pass + reconnect passes share the same loop body so the
  // success/failure handling is identical regardless of pass index.
  while (!runEnded && !signal.aborted) {
    try {
      await streamOnce(cursor, wrappedOnEvent, signal);
      lastError = undefined;
    } catch (e) {
      lastError = e;
    }
    if (runEnded) return;          // server told us the run is over
    if (signal.aborted) return;    // operator pulled the plug

    // Stream closed without a terminal signal — reconnect.
    attempts++;
    if (attempts > maxRetries) {
      const msg = lastError
        ? `SSE stream lost after ${maxRetries} reconnects: ${String((lastError as any)?.message ?? lastError)}`
        : `SSE stream lost after ${maxRetries} reconnects (stream closed without run-end)`;
      throw new Error(msg);
    }
    // Backoff before the next attempt. signal.aborted is re-checked
    // post-sleep so an abort during the wait short-circuits cleanly.
    const ms = backoff[Math.min(attempts - 1, backoff.length - 1)];
    await sleep(ms);
  }
}
